/**
 * Product Custom Button Handler
 * Handles calculator, quote, and general button interactions
 * Compatible with Nino's Handy Tags theme architecture
 */

'use strict';

class ProductCustomButton {
  constructor() {
    this.buttons = document.querySelectorAll('.product__custom-button button');
    this.init();
  }

  init() {
    if (this.buttons.length === 0) return;
    
    this.bindEvents();
    this.setupAnalytics();
    
    // Debug logging for development
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('.myshopify.com')) {
      console.log(`ProductCustomButton: Initialized ${this.buttons.length} buttons`);
    }
  }

  bindEvents() {
    this.buttons.forEach(button => {
      // Calculator buttons
      if (button.dataset.calculatorAction) {
        button.addEventListener('click', this.handleCalculatorClick.bind(this));
      }
      
      // Quote buttons  
      if (button.dataset.quoteAction) {
        button.addEventListener('click', this.handleQuoteClick.bind(this));
      }
      
      // Generic click tracking for all buttons
      button.addEventListener('click', this.trackButtonClick.bind(this));
    });
  }

  handleCalculatorClick(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const productId = button.dataset.calculatorProduct;
    const productHandle = button.dataset.productHandle;

    this.setButtonState(button, 'loading');

    try {
      // Detect product type: styled vs regular
      const isStyledProduct =
        document.querySelector('.ht-calculator-styled-form') !== null ||
        (window.htCalculatorStyled && window.htCalculatorStyled.form) ||
        document.querySelector('[data-calculator-type="styled"]') !== null;

      console.log('🔘 Calculator button clicked');
      console.log('📦 Product type detected:', isStyledProduct ? 'Styled' : 'Regular');

      let targetElement = null;

      if (isStyledProduct) {
        // For styled products: scroll to gallery carousel
        targetElement = document.querySelector('.ht-gallery-carousel');
        if (targetElement) {
          console.log('📍 Scrolling to gallery carousel...');
        }
      } else {
        // For regular products: scroll to calculator wrapper
        targetElement = document.querySelector('.ht-calculator-wrapper, #ht-calculator, .ht-calculator, [id*="calculator"]');
        if (targetElement) {
          console.log('📍 Scrolling to calculator wrapper...');
        }
      }

      if (targetElement) {
        // Calculate offset for fixed headers
        const headerHeight = document.querySelector('header, .header, .site-header')?.offsetHeight || 0;
        const scrollOffset = headerHeight + 20;

        const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - scrollOffset;

        // Smooth scroll to target
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        // Add highlight effect
        targetElement.style.boxShadow = '0 0 20px rgba(255, 165, 0, 0.5)';
        setTimeout(() => {
          targetElement.style.boxShadow = '';
        }, 3000);

        this.setButtonState(button, 'success');

        // Reset button state after 2 seconds
        setTimeout(() => {
          this.setButtonState(button, 'default');
        }, 2000);

        console.log('✅ Scrolled to target section');

      } else {
        // Fallback: Try to trigger existing calculator if available
        if (window.HTCalculator || window.htCalculatorStyled) {
          console.log('🚀 Opening calculator...');

          const calculator = window.htCalculatorStyled || window.HTCalculator;

          // Try different methods to open/activate calculator
          if (typeof calculator.show === 'function') {
            calculator.show();
          } else if (typeof calculator.open === 'function') {
            calculator.open();
          } else if (typeof calculator.activate === 'function') {
            calculator.activate();
          }

          this.setButtonState(button, 'success');

        } else {
          // Final fallback - redirect to dedicated calculator page
          console.log('🔄 Redirecting to calculator page...');

          const fallbackUrl = button.dataset.fallbackUrl ||
                              `/pages/calculator?product=${productHandle}` ||
                              `/collections/all/products/${productHandle}#calculator`;

          window.location.href = fallbackUrl;
        }
      }

    } catch (error) {
      console.error('❌ Calculator error:', error);
      this.setButtonState(button, 'error');
      this.showErrorMessage(button, 'Calculator temporarily unavailable');
    }

    // Analytics
    this.trackEvent('calculator_button_clicked', {
      product_id: productId,
      product_handle: productHandle,
      success: true
    });
  }

  handleQuoteClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const productId = button.dataset.quoteProduct;
    const productHandle = button.dataset.productHandle;
    
    this.setButtonState(button, 'loading');
    
    // Check if quote module is available
    if (window.QuoteRequest) {
      try {
        window.QuoteRequest.open({
          productId: productId,
          productHandle: productHandle,
          trigger: 'product-button'
        });
        
        this.setButtonState(button, 'success');
        
        setTimeout(() => {
          this.setButtonState(button, 'default');
        }, 2000);
        
      } catch (error) {
        console.error('Quote request error:', error);
        this.setButtonState(button, 'error');
        this.showErrorMessage(button, 'Quote request temporarily unavailable');
      }
    } else {
      // Fallback: redirect to contact form
      const fallbackUrl = `/pages/contact?subject=Quote Request&product=${productHandle}`;
      window.location.href = fallbackUrl;
    }
    
    // Analytics
    this.trackEvent('quote_button_clicked', {
      product_id: productId,
      product_handle: productHandle
    });
  }

  setButtonState(button, state) {
    const container = button.closest('.product__custom-button');
    
    // Remove all state classes
    container.removeAttribute('data-loading');
    container.removeAttribute('data-success');
    container.removeAttribute('data-error');
    
    // Add new state
    switch (state) {
      case 'loading':
        container.setAttribute('data-loading', 'true');
        button.setAttribute('aria-disabled', 'true');
        break;
      case 'success':
        container.setAttribute('data-success', 'true');
        button.setAttribute('aria-disabled', 'false');
        break;
      case 'error':
        container.setAttribute('data-error', 'true');
        button.setAttribute('aria-disabled', 'false');
        break;
      default:
        button.setAttribute('aria-disabled', 'false');
    }
  }

  showErrorMessage(button, message) {
    // Create or update error message
    let errorMsg = button.parentNode.querySelector('.button-error-message');
    
    if (!errorMsg) {
      errorMsg = document.createElement('div');
      errorMsg.className = 'button-error-message';
      errorMsg.style.cssText = `
        margin-top: 0.8rem;
        padding: 0.8rem 1.2rem;
        background: rgb(239, 68, 68, 0.1);
        border: 1px solid rgb(239, 68, 68, 0.2);
        border-radius: 0.4rem;
        color: rgb(185, 28, 28);
        font-size: 1.4rem;
        text-align: center;
      `;
      button.parentNode.appendChild(errorMsg);
    }
    
    errorMsg.textContent = message;
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (errorMsg && errorMsg.parentNode) {
        errorMsg.remove();
      }
      this.setButtonState(button, 'default');
    }, 5000);
  }

  trackButtonClick(event) {
    const button = event.currentTarget;
    const buttonText = button.querySelector('.button__text')?.textContent || 'Unknown';
    const productId = button.dataset.calculatorProduct || button.dataset.quoteProduct;
    
    // Track generic button click
    this.trackEvent('product_button_clicked', {
      button_text: buttonText,
      product_id: productId,
      button_type: button.dataset.calculatorAction ? 'calculator' : 
                   button.dataset.quoteAction ? 'quote' : 'link'
    });
  }

  trackEvent(eventName, properties = {}) {
    // Shopify Analytics
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.publish) {
      window.ShopifyAnalytics.publish(eventName, {
        ...properties,
        timestamp: Date.now(),
        page_url: window.location.href
      });
    }
    
    // Google Analytics 4 (if available)
    if (window.gtag) {
      window.gtag('event', eventName, properties);
    }
    
    // Facebook Pixel (if available)
    if (window.fbq) {
      window.fbq('trackCustom', eventName, properties);
    }
    
    // Debug logging
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('.myshopify.com')) {
      console.log('Button Event:', eventName, properties);
    }
  }

  setupAnalytics() {
    // Initialize any analytics tracking
    if (window.ShopifyAnalytics) {
      // Track button impression
      this.buttons.forEach(button => {
        const productId = button.dataset.calculatorProduct || button.dataset.quoteProduct;
        if (productId) {
          this.trackEvent('product_button_viewed', {
            product_id: productId,
            button_type: button.dataset.calculatorAction ? 'calculator' : 'quote'
          });
        }
      });
    }
  }

  // Public method to manually trigger button actions (for testing)
  triggerButton(buttonSelector, action = 'click') {
    const button = document.querySelector(buttonSelector);
    if (button) {
      button.dispatchEvent(new Event(action));
      return true;
    }
    return false;
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ProductCustomButtonHandler = new ProductCustomButton();
  });
} else {
  window.ProductCustomButtonHandler = new ProductCustomButton();
}

// Expose for theme integration
window.ProductCustomButton = ProductCustomButton;